import ClockCircleIcon from '@/images/ClockCircle.svg';
import DialogIcon from '@/images/Dialog.svg';
import { useGetInteractionsAggregates } from '@/pages/agents/api/insights';
import { InteractionAggregatesBucket } from '@d-id/types';
import { useState } from 'react';
import { useAgentInsightsContext } from '../../contex/useAgentInsightsContext';
import { ConversationDrawer } from '../ConversationDrawer';
import { InsightsColumn, Table, TableColumnsHeader, TableHeader, TableNoData } from '../Table';
import { ConversationRow } from './ConversationRow';
import { ConversationRowSkeleton } from './ConversationRowSkeleton';
import CalendarMinimalisticIcon from './icons/CalendarMinimalistic.svg';
import DashboardIcon from './icons/Dashboard.svg';
import SmileCircleIcon from './icons/SmileCircle.svg';

const conversationsTableColumns: InsightsColumn[] = [
    { label: 'User Query', icon: DialogIcon, grow: 2.5 },
    {
        label: 'Satisfaction',
        icon: SmileCircleIcon,
        grow: 1,
        tooltip: "Signals extracted from the conversation indicating how satisfied the user is with the Agent's response",
    },
    { label: 'Date', icon: CalendarMinimalisticIcon, grow: 1 },
    { label: 'Duration', icon: ClockCircleIcon, grow: 1 },
    { label: 'Interactions', icon: DashboardIcon, minWidth: 110 },
];

export const ConversationsTable = () => {
    const { dateRange, from, to, agent_ids, shareId, isPublic, conversationTopicFilter, conversationIntentFilter } =
        useAgentInsightsContext();
    const [conversation, setConversation] = useState<InteractionAggregatesBucket | null>(null);

    const { data, isLoading } = useGetInteractionsAggregates({
        dateRange,
        from,
        to,
        agent_ids,
        shareId,
        isPublic,
        group_by: { kind: 'conversation_user_query' },
        additional_group_bys: [{ kind: 'conversation_id' }],
        filters: [
            ...(conversationTopicFilter ? [{ kind: 'conversation_topic', values: [conversationTopicFilter] }] : []),
            ...(conversationIntentFilter ? [{ kind: 'conversation_user_action', values: [conversationIntentFilter] }] : []),
        ],
        variables: [
            'n_users',
            'n_sessions',
            'conversation_user_feedback',
            'conversation_sentiment_score',
            'session_length',
            'session_time',
            'start_session',
            'users',
            'conversation',
        ],
    });
    const conversations = data?.data;

    const handleRowClick = (item: InteractionAggregatesBucket) => {
        setConversation(item);
    };

    const conversationId = conversation?.additional_groups?.[0];
    return (
        <Table>
            <TableHeader title="Conversations" />
            <TableColumnsHeader columns={conversationsTableColumns} />

            {/* loading */}
            {isLoading && (
                <>
                    <ConversationRowSkeleton columns={conversationsTableColumns} sizes={[100, 100, 50, 50, 50]} />
                    <ConversationRowSkeleton columns={conversationsTableColumns} sizes={[70, 70, 30, 30, 30]} />
                </>
            )}

            {/* display data */}
            {!isLoading &&
                conversations?.map(item => (
                    <ConversationRow
                        key={`${item.group_name}-${item.additional_groups?.[0]}`}
                        columns={conversationsTableColumns}
                        item={item}
                        onClick={() => handleRowClick(item)}
                    />
                ))}

            {/* no data available */}
            {!isLoading && !conversations?.length && <TableNoData />}

            <ConversationDrawer
                groupName={conversation?.group_name || ''}
                conversationId={conversationId || null}
                onClose={() => setConversation(null)}
            />
        </Table>
    );
};
